import { TableTextFilter } from "components/TableTextFilter/TableTextFilter";
import { setScrollBehavior } from "features/scrollPageBehaviorProvider/scrollPageBehaviorProvider";
import { _ } from "i18n";
import type { DiscoverClubHousesRowResponse } from "libs/apiClient";
import client from "libs/client";
import { useEffect, useState } from "react";
import DatagridV2, { type SimpleColumn } from "ui-kit/src/Datagrid/DatagridV2";
import { DiscoverBreadcrumbs } from "features/discover/DiscoverBreadcrumbs";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import { ClubHouseTypeChoices } from "features/discover/types";
import {createSortArrows, formatDateTime} from "features/discover/functions";
import {Link} from "react-router";

const entityTypeOptions = [
	{
		label: 'Discover',
		value: 'Discover',
	},
	{
		label: 'Partner',
		value: 'Partner',
	},
	{
		label: 'NonPartner',
		value: 'NonPartner',
	},
];

const eliteTypeOptions = [
	{
		label: "League",
		value: "league",
	},
	{
		label: "National",
		value: "national",
	},
];

export function DiscoverClubHousesPage() {
    const [pagination, setPagination] = useState<{ pageIndex: number; pageSize: number }>({
        pageIndex: 0,
        pageSize: 15,
    });

    const [filters, setFilters] = useState<{ [key: string]: string | number | undefined }>({});
    const [filterValues, setFilterValues] = useState<{ [key: string]: string | undefined }>({});
    const [sort, setSort] = useState<{ field: string, direction: string }>();

    useEffect(() => { setScrollBehavior() }, [])

	const { data, isLoading } = client.getDiscoverClubHouses.useQuery({
		page: pagination.pageIndex + 1, limit: pagination.pageSize, filters: [
			...(filters?.id ? [{
				field: "id",
				type: "search",
				value: filters.id,
			}] : []),
			...(filters?.name ? [{
				field: "name",
				type: "search",
				value: filters.name,
			}] : []),
			...(filters?.type ? [{
				field: "type",
				type: "exact",
				value: filters.type,
			}] : []),
			...(filters?.entityType ? [{
				field: "entityType",
				type: "exact",
				value: filters.entityType,
			}] : []),
			...(filters?.country ? [{
				field: "country",
				type: "exact",
				value: filters.country,
			}] : []),
			...(filters?.city ? [{
				field: "city",
				type: "search",
				value: filters.city,
			}] : []),
			...(filters?.eliteType ? [{
				field: "isNational",
				type: "boolean",
				value: filters.eliteType === "national",
			}] : []),
		],
		order: sort
	})

	const { data: countries, isLoading: isCountriesLoading } = client.getDiscoverCountries.useQuery({})

	const handleSetFilter = (key: string, value?: number | string) => {
        setFilters({ ...filters, [key]: value });
        setPagination({ ...pagination, pageIndex: 0 });
    }

    const handleClearFilter = (key: string) => {
        setFilters({ ...filters, [key]: undefined })
    }

	const handleSort = (newSort?: {field: string, direction: string}) => {
		setSort(newSort);
	}

    const generateColumnHeader = (columnName: string, label: string, className?: string, isNumber?: boolean) => () => (
        <div className={`tableHeader ${className}`}>
            <TableTextFilter
                value={filterValues[columnName] || ""}
                onChange={(value) => setFilterValues({ ...filterValues, [columnName]: value })}
                number={isNumber}
                clear={() => {
                    handleClearFilter(columnName);
                    setFilterValues({ ...filterValues, [columnName]: undefined })
                }}
                onSubmit={(value: string) => handleSetFilter(columnName, value)}
                placeholder={label}
            />
			{createSortArrows(columnName, sort, handleSort)}
        </div>
    );

    const simpleColumns: SimpleColumn<DiscoverClubHousesRowResponse>[] = [
		{
			id: "id",
			key: "id",
			header: generateColumnHeader("id", "ID", "tableHeader--id", true)
		},
		{
			id: "entityType",
			key: "entityType",
			header: () => <div className="tableHeader">
				<SelectComponent
					value={entityTypeOptions.find((o) => o.value === filters?.entityType)}
					handleChange={(value) => handleSetFilter("entityType", value?.value)}
					placeholder="Type"
					items={entityTypeOptions}
				/>
			</div>,
			cell: (value) => <div className="thinCell">{value || '-'}</div>
		},
		{
			id: "name",
			key: "name",
			header: generateColumnHeader("name", "Name"),
		},
		{
			id: "type",
			key: "type",
			header: () => <div className="tableHeader">
				<SelectComponent
					value={ClubHouseTypeChoices.find((o) => o.value === filters?.type)}
					handleChange={(value) => handleSetFilter("type", value?.value)}
					placeholder="Discover type"
					items={ClubHouseTypeChoices}
				/>
				{createSortArrows("type", sort, handleSort)}
			</div>,
			cell: (value) => <div className="thinCell">{value || '-'}</div>
		},
        {
            id: "country",
            key: "country",
			header: () => <div className="tableHeader">
				<SelectComponent
					value={(countries?.data || []).find((o) => o.value === filters?.country)}
					handleChange={(value) => handleSetFilter("country", value?.value)}
					placeholder="Country"
					items={countries?.data || []}
					isLoading={isCountriesLoading}
				/>
				{createSortArrows("country", sort, handleSort)}
			</div>,
			cell: (value) => <div className="thinCell">{value || '-'}</div>
		},
		{
			id: "region",
            key: "region",
			header: "region",
			cell: (value) => <div className="thinCell">{value || '-'}</div>
        },
		{
			id: "city",
			key: "city",
			header: generateColumnHeader("city", "City"),
			cell: (value) => <div className="thinCell">{value || '-'}</div>
		},
		{
			id: "teams",
			key: "numberOfTeams",
			header: "Teams claimed/real",
			cell: (value, row) => <div className="thinCell">{`${row.numberOfTeams || '-'} / ${row.realNumberOfTeams}`}</div>
		},
		{
			id: "eliteType",
			key: "eliteType",
			header: () => <div className="tableHeader">
				<SelectComponent
					value={eliteTypeOptions.find((o) => o.value === filters?.eliteType)}
					handleChange={(value) => handleSetFilter("eliteType", value?.value)}
					placeholder="Elite type"
					items={eliteTypeOptions}
				/>
			</div>,
			cell: (value, row) => row.entityType !== "Discover" ? (row.isNational ? 'National' : 'League') : '-'
		},
		{
			id: "createdAt",
			key: "createdAt",
			header: () => <div className="tableHeader">
				<span>Created at</span>
				{createSortArrows("createdAt", sort, handleSort)}
			</div>,
			cell: (value) => <div className="thinCell">{formatDateTime(value as string | null)}</div>
		},
	]

	return (
		<div className="discover-container discover-container--wide">
			<div className="pageHeading">
				<DiscoverBreadcrumbs items={[
					{
						label: 'Clubs',
					},
				]}/>
				<div>
					<Link className="admin-button admin-button--primary" to="/admin/clubs-amateur/add">Create club (partner)</Link>
				</div>
			</div>
			<div className="discover-card discover-card--for-table">
				<DatagridV2
					getRowLink={(row) => `/discover/club/${row.id}`}
					isLoading={isLoading}
					simpleColumns={simpleColumns}
					data={data?.data.items ?? []}
					pageCount={data?.data?.pages ?? 1}
					pagination={pagination}
					setPagination={setPagination}
					noResults={_("datatables.zero_records")}
				/>
			</div>
		</div>
	)
}
